import React, { useEffect, useMemo, useState } from "react";
import {
  ChevronDownIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { useNavigate, useParams } from "react-router-dom";

import { sitePath } from "../../utils/urls";

const DRAWER_MS = 300;

const TableRowDrawer = ({ table, row, baseline, readOnly = false, onSaved }) => {
  const navigate = useNavigate();
  const { siteSlug, "*": rest } = useParams();
  const pathParts = (rest || "").split("/").filter(Boolean);
  const tableHash = pathParts[0];
  const rowIndex = pathParts[1] === "rows" ? pathParts[2] : undefined;
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState(row?.data || {});
  const [metaOpen, setMetaOpen] = useState(false);
  const columns = useMemo(
    () => (table?.schema || []).filter((column) => column.name && column.name !== "_meta"),
    [table?.schema],
  );
  const isFormTable = table?.source === "form";
  const meta = values?._meta && typeof values._meta === "object" ? values._meta : null;
  const classification = meta?.classification || "";
  const isSecurity = classification === "security";
  const isSpam = classification === "spam";
  const approved = Boolean(meta?.approved);
  const baselineApproved = Boolean(
    baseline?._meta && typeof baseline._meta === "object"
      ? baseline._meta.approved
      : false,
  );
  const approvedChanged = isFormTable && approved !== baselineApproved;

  useEffect(() => {
    setValues(row?.data || {});
    setMetaOpen(false);
  }, [rowIndex, row?.data]);

  useEffect(() => {
    const frame = requestAnimationFrame(() => setOpen(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  const close = () => {
    setOpen(false);
    window.setTimeout(() => navigate(sitePath(siteSlug, `data/${tableHash}/`)), DRAWER_MS);
  };

  const save = (event) => {
    event.preventDefault();
    if (readOnly || isSecurity || rowIndex == null || !table) return;
    const payload = {};
    columns.forEach((column) => {
      payload[column.name] = values[column.name] ?? "";
    });
    if (values._meta && typeof values._meta === "object") {
      payload._meta = values._meta;
    }
    onSaved?.(Number(rowIndex), payload);
    close();
  };

  const toggleApproved = () => {
    if (readOnly || isSecurity || rowIndex == null || !isFormTable) return;
    const base = row?.data || {};
    const baseMeta = base._meta && typeof base._meta === "object" ? base._meta : {};
    const next = {
      ...base,
      _meta: { ...baseMeta, approved: !baseMeta.approved },
    };
    setValues((current) => ({
      ...current,
      _meta: next._meta,
    }));
    onSaved?.(Number(rowIndex), next);
  };

  if (!table) return null;

  return (
    <div className="fixed inset-0 z-50">
      <button
        type="button"
        aria-label="Close row editor"
        onClick={close}
        className={`absolute inset-0 bg-theme-text transition-opacity duration-300 ease-out ${
          open ? "opacity-25" : "opacity-0"
        }`}
      />
      <aside
        className={`absolute inset-y-0 right-0 flex w-full max-w-md flex-col border-l border-theme-secondary bg-theme-main shadow-2xl transition-transform duration-300 ease-out ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between border-b border-theme-secondary px-5 py-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-theme-text opacity-50">Row {rowIndex}</p>
            <h2 className="font-semibold text-theme-text">{readOnly || isSecurity ? "Row" : "Edit row"}</h2>
          </div>
          <button
            type="button"
            onClick={close}
            className="rounded-lg px-2 py-1 text-sm text-theme-text opacity-60 hover:bg-black hover:bg-opacity-5 hover:opacity-100"
          >
            Close
          </button>
        </div>
        <form onSubmit={save} className="flex flex-1 flex-col overflow-y-auto px-5 py-4">
          {row && isSecurity && (
            <div className="mb-4 flex items-start gap-2.5 rounded-lg border border-red-500/40 bg-red-500/15 px-3 py-2.5 text-sm text-red-600">
              <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
              <span>Flagged by security review. This row cannot be included or approved.</span>
            </div>
          )}
          {row && isSpam && (
            <div className="mb-4 flex items-start gap-2.5 rounded-lg bg-amber-400/15 px-3 py-2.5 text-sm text-theme-text">
              <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-500" aria-hidden="true" />
              <span>Flagged as spam, can still be approved</span>
            </div>
          )}
          {row && isFormTable && (
            <div
              className={`mb-4 flex items-center justify-between rounded-lg border border-theme-secondary px-3 py-2.5 ${
                approvedChanged ? "bg-amber-400/25" : ""
              }`}
            >
              <span className="text-xs font-semibold text-theme-text opacity-60">Approved</span>
              <button
                type="button"
                role="switch"
                aria-checked={approved}
                disabled={readOnly || isSecurity}
                onClick={toggleApproved}
                className={`relative h-5 w-9 flex-shrink-0 rounded-full transition disabled:cursor-not-allowed disabled:opacity-40 ${
                  approved ? "bg-theme-primary" : "bg-theme-secondary"
                }`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white shadow transition ${
                    approved ? "translate-x-4" : ""
                  }`}
                />
              </button>
            </div>
          )}
          {row ? columns.map((column) => (
            <label key={column.name} className="mb-4 block">
              <span className="mb-1 block text-xs font-semibold text-theme-text opacity-60">{column.name}</span>
              <input
                value={values[column.name] ?? ""}
                readOnly={readOnly || isSecurity}
                onChange={(event) => setValues((current) => ({
                  ...current,
                  [column.name]: event.target.value,
                }))}
                className="w-full rounded-lg border border-theme-secondary bg-theme-main px-3 py-2 text-sm text-theme-text"
              />
            </label>
          )) : (
            <p className="text-sm text-theme-text opacity-60">This row is not in the loaded table.</p>
          )}
          {meta && (
            <div className="mb-4">
              <button
                type="button"
                onClick={() => setMetaOpen((openMeta) => !openMeta)}
                className="flex w-full items-center justify-between text-left"
              >
                <span className="text-xs font-semibold text-theme-text opacity-60">Metadata</span>
                <ChevronDownIcon
                  className={`h-4 w-4 text-theme-text opacity-50 transition ${metaOpen ? "rotate-180" : ""}`}
                  aria-hidden="true"
                />
              </button>
              {metaOpen && (
                <dl className="mt-2 space-y-1 rounded-lg border border-theme-secondary bg-theme-secondary px-3 py-3 text-xs text-theme-text">
                  {Object.entries(meta).map(([key, value]) => (
                    <div key={key} className="flex justify-between gap-3">
                      <dt className="opacity-60">{key}</dt>
                      <dd className="truncate font-medium">{String(value)}</dd>
                    </div>
                  ))}
                </dl>
              )}
            </div>
          )}
          <div className="mt-auto flex gap-2 border-t border-theme-secondary pt-4">
            <button
              type="button"
              onClick={close}
              className="flex-1 rounded-lg border border-theme-secondary px-4 py-2 text-sm text-theme-text hover:bg-black hover:bg-opacity-5"
            >
              {readOnly || isSecurity ? "Close" : "Cancel"}
            </button>
            {!readOnly && !isSecurity && (
              <button
                type="submit"
                disabled={!row}
                className="flex-1 rounded-lg bg-theme-primary px-4 py-2 text-sm font-semibold text-theme-button disabled:opacity-50"
              >
                Save
              </button>
            )}
          </div>
        </form>
      </aside>
    </div>
  );
};

export default TableRowDrawer;
